import { useMemo } from 'react'
import { useMealCategorySnapshot } from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'

export interface CategoryOption {
  value: string
  label: string
}

/**
 * Active meal categories from the `menu/meal_categories` projection, by label.
 *
 * Every category picker reads this list. It arrives with the rest of the live
 * data, so a picker no longer sends its own request, and a category operations
 * adds reaches open screens on the next sweep. Inactive rows ride the
 * projection with `is_active: false` and are dropped here. Outside the live
 * runtime the store holds generated dummy rows, so this returns nothing and
 * callers keep their built-in fallback.
 */
export const useMealCategories = (): CategoryOption[] => {
  const snapshot = useMealCategorySnapshot() as { records: readonly Record<string, unknown>[] }
  const live = isLiveRuntime()
  return useMemo(() => {
    if (!live) return []
    return snapshot.records
      .filter((row) => row.is_active !== false && String(row.slug ?? '') !== '')
      .map((row) => ({ value: String(row.slug), label: String(row.label ?? row.slug) }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [snapshot.records, live])
}

let policy: Promise<boolean> | null = null

/**
 * Whether chef verification is paused: a server setting, not row data, so it
 * is a command rather than a projection. Read once and shared by every screen;
 * `refresh` reads it again. A failed read is not kept.
 */
export const readVerificationPaused = (refresh = false): Promise<boolean> => {
  if (!policy || refresh) {
    const request = Promise.resolve().then(() => commands.menu.catalogPolicy()).then((response) => {
      const paused = commandPayload(response).verification_paused
      if (typeof paused !== 'boolean') throw new Error('Kitchen setup could not be loaded.')
      return paused
    })
    request.catch(() => { if (policy === request) policy = null })
    policy = request
  }
  return policy
}

/** Test seam: forget the shared policy read. */
export const resetCatalogPolicy = (): void => { policy = null }
